import { useQuery } from "@tanstack/react-query";
import { getUserIdToken } from "../utils/auth";

/** Keep in sync with functions/library/support-article-slug.js */
function normalizeSupportArticleSlug(input) {
  let s = String(input ?? "")
    .trim()
    .toLowerCase();
  try {
    s = s.normalize("NFKC");
  } catch (_) {
    /* ignore */
  }
  return s.replace(/[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g, "-");
}

const API_BASE = "https://app.mighty45.com/api/v1/learning-articles";
const COACH_USER_TYPE = "coach";

async function fetchLearningArticles() {
  const token = await getUserIdToken();
  const params = new URLSearchParams({ user_type: COACH_USER_TYPE });
  const res = await fetch(`${API_BASE}?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to fetch learning articles");
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Unknown error");
  return data.articles;
}

async function fetchArticleBySlug(slug) {
  const token = await getUserIdToken();
  const slugNorm = normalizeSupportArticleSlug(slug);
  const params = new URLSearchParams({
    slug: slugNorm,
    user_type: COACH_USER_TYPE,
  });
  const res = await fetch(`${API_BASE}?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Article not found");
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Unknown error");
  return data.article;
}

export function useLearningArticles() {
  return useQuery({
    queryKey: ["learning-articles"],
    queryFn: fetchLearningArticles,
    staleTime: 10 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useLearningArticle(slug, options = {}) {
  const extraEnabled = options.enabled !== false;
  return useQuery({
    queryKey: ["learning-article", slug],
    queryFn: () => fetchArticleBySlug(slug),
    enabled: !!slug && extraEnabled,
    staleTime: 10 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
